// Copyright 2026 Zibby Lab. Apache-2.0.
//
// DangerZone — shared destructive-action section for detail pages.
//
// Renders the "Danger zone" heading + an explanatory hint + a DangerButton
// that opens the styled DestroyAppModal (NOT a native window.confirm). Used
// by both the pipeline Settings tab and the app Settings tab so teardown is
// visually + behaviourally identical across the product.
//
// It owns the modal open/submitting/error state. The caller supplies an async
// `onConfirm` that performs the actual delete (and typically navigates away on
// success); DangerZone drives the busy state and closes the modal when it
// resolves.
//
// THE MODAL IS THE PROGRESS INDICATOR. A destructive action here can be a
// server-side CASCADE that takes seconds (deleting a composite agent walks its
// member rows first), so the dialog deliberately stays mounted for the whole
// request: spinner on the confirm button, dismissal inert, `busyMessage` in
// place of the warning. And a REJECTION keeps it open with the error printed
// inside it — closing the dialog is the app saying "done", and it must not say
// that for a delete that failed. (Founder report, 2026-08-26.)
//
// Usage (cloud app — plain confirm):
//   <DangerZone
//     label="n8n"
//     hint="This stops the running task and removes the app. This cannot be undone."
//     buttonLabel="Destroy app"
//     onConfirm={() => Apps.destroy(instanceId)}
//   />
//
// Usage (solo app — type-to-confirm data-loss gate):
//   <DangerZone label={slug} requireType buttonLabel="Delete app" onConfirm={...} />

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { trashOutline } from 'ionicons/icons';

import DangerButton from '../DangerButton/DangerButton';
import DestroyAppModal from '../SoloDestroyModal/SoloDestroyModal';
import './DangerZone.css';

export default function DangerZone({
  // When true (default), render a separator rule above the zone — right
  // when it follows other sections. Set false when it stands alone in its
  // own card so the line doesn't look like a mistake.
  separated = true,
  // Section copy.
  heading = 'Danger zone',
  hint,
  buttonLabel,
  // Identity shown in the modal title / type-to-confirm word.
  label,
  // Modal overrides (all optional — DestroyAppModal derives sensible
  // defaults from `label` + `requireType`).
  modalTitle,
  modalWarning,
  requireType = false,
  confirmWord,
  confirmLabel,
  busyLabel,
  // What the modal says while the work is in flight, in place of the warning.
  busyMessage,
  // The actual delete. Async. A REJECTION means "not done": the modal stays
  // open, shows the rejection's own message, and hands the controls back.
  onConfirm,
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // The last attempt's failure, shown IN the dialog — next to the thing it is
  // about, rather than in a toast the user has to chase while the dialog they
  // are looking at says nothing went wrong.
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    setSubmitting(true);
    // A new attempt makes the previous failure untrue — clear it before the
    // spinner, never after, or the old error sits under the new spinner.
    setError('');
    try {
      await onConfirm?.();
      setOpen(false);
    } catch (err) {
      // onConfirm re-throws to signal "not done" — keep the modal open, and
      // say WHY. An error carrying no message stringifies to the bare word
      // "Error", which tells the user nothing.
      const msg = String(err?.message ?? err ?? '').trim();
      setError(!msg || msg === 'Error' ? 'The server did not say why.' : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`danger-zone${separated ? ' danger-zone--separated' : ''}`}>
      <h3 className="danger-zone-title">{heading}</h3>
      {hint && <p className="danger-zone-hint">{hint}</p>}
      <DangerButton icon={trashOutline} onClick={() => { setError(''); setOpen(true); }}>
        {buttonLabel}
      </DangerButton>

      <DestroyAppModal
        open={open}
        label={label}
        title={modalTitle}
        warning={modalWarning}
        requireType={requireType}
        confirmWord={confirmWord}
        confirmLabel={confirmLabel}
        busyLabel={busyLabel}
        busyMessage={busyMessage}
        errorText={error}
        busy={submitting}
        onClose={() => { if (!submitting) { setOpen(false); setError(''); } }}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

DangerZone.propTypes = {
  separated: PropTypes.bool,
  heading: PropTypes.string,
  hint: PropTypes.node,
  buttonLabel: PropTypes.string.isRequired,
  label: PropTypes.string,
  modalTitle: PropTypes.string,
  modalWarning: PropTypes.node,
  requireType: PropTypes.bool,
  confirmWord: PropTypes.string,
  confirmLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  busyMessage: PropTypes.node,
  onConfirm: PropTypes.func.isRequired,
};
